import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, KeyRound, RotateCcw } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SupportWidget from '../components/SupportWidget';
import { getOAuthErrorMessage } from '../lib/utils';
import { TEMPLATE_LOOKUP, resolveTemplateId } from '../lib/weddingTemplates';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../lib/analytics';
import { getMarketingSeoConfig } from '../lib/seo';

const TEMPLATE_PREVIEW_FALLBACK = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [partnerCode, setPartnerCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerStep, setRegisterStep] = useState('form');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorChallengeId, setTwoFactorChallengeId] = useState('');
  const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending2FA, setResending2FA] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { register, verifyTwoFactor, resendTwoFactorCode, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seoConfig = getMarketingSeoConfig('register');

  useEffect(() => {
    const couponFromUrl = (searchParams.get('coupon') || '').trim().toUpperCase();
    const couponFromStorage = (sessionStorage.getItem('partnerCouponCode') || '').trim().toUpperCase();
    const initialCoupon = couponFromUrl || couponFromStorage;
    if (initialCoupon) {
      setPartnerCode(initialCoupon);
      sessionStorage.setItem('partnerCouponCode', initialCoupon);
    }
  }, [searchParams]);

  useEffect(() => {
    const templateFromUrl = searchParams.get('template');
    const templateFromStorage = sessionStorage.getItem('selectedTemplateId');
    const rawTemplateId = templateFromUrl || templateFromStorage || '';
    const resolvedTemplateId = rawTemplateId ? resolveTemplateId(rawTemplateId) : '';

    if (resolvedTemplateId && TEMPLATE_LOOKUP[resolvedTemplateId]) {
      setSelectedTemplateId(resolvedTemplateId);
      sessionStorage.setItem('selectedTemplateId', resolvedTemplateId);
      return;
    }

    setSelectedTemplateId('');
  }, [searchParams]);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (oauthError) {
      toast.error(getOAuthErrorMessage(oauthError));
    }
  }, [searchParams]);

  const handleGoogleRegister = () => {
    trackEvent('sign_up_start', {
      method: 'google',
      template_id: selectedTemplateId || undefined,
      partner_code: partnerCode.trim().toUpperCase() || undefined,
    });
    loginWithGoogle(editorTarget, {
      flow: 'register',
      partnerCode,
    });
  };

  const selectedTemplate = selectedTemplateId ? TEMPLATE_LOOKUP[selectedTemplateId] : null;
  const editorTarget = selectedTemplateId ? `/editor?template=${encodeURIComponent(selectedTemplateId)}` : '/dashboard';
  const templatePreviewTarget = selectedTemplateId ? `/s/exemplo?template=${encodeURIComponent(selectedTemplateId)}` : '/';

  const handleSubmit = async (e) => {
    e.preventDefault();

    trackEvent('sign_up_start', {
      method: 'email',
      template_id: selectedTemplateId || undefined,
      partner_code: partnerCode.trim().toUpperCase() || undefined,
    });

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const normalizedPartnerCode = partnerCode.trim().toUpperCase();
      const result = await register(email.trim().toLowerCase(), password, name.trim(), normalizedPartnerCode);
      if (result?.requires_2fa) {
        setRegisterStep('verify_2fa');
        setTwoFactorChallengeId(result.challenge_id || '');
        setTwoFactorMaskedEmail(result.masked_email || email);
        setTwoFactorCode('');
        if (normalizedPartnerCode) {
          sessionStorage.setItem('partnerCouponCode', normalizedPartnerCode);
        }
        toast.success('Código de verificação enviado para o seu e-mail.');
        return;
      }

      if (normalizedPartnerCode) {
        sessionStorage.removeItem('partnerCouponCode');
      }
      trackEvent('sign_up_complete', {
        method: 'email',
        template_id: selectedTemplateId || undefined,
      });
      toast.success('Conta criada com sucesso!');
      const pendingAffiliateCode = sessionStorage.getItem('affiliateCode');
      const pendingPlan = sessionStorage.getItem('selectedPlan');
      if (pendingPlan) {
        const qs = pendingAffiliateCode ? `?ref=${encodeURIComponent(pendingAffiliateCode)}` : '';
        navigate(`/planos${qs}`);
        return;
      }
      navigate(editorTarget);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault();
    if ((twoFactorCode || '').replace(/\D/g, '').length !== 6) {
      toast.error('Informe o código de 6 dígitos enviado para o seu e-mail.');
      return;
    }

    setVerifying2FA(true);
    try {
      await verifyTwoFactor(twoFactorChallengeId, twoFactorCode, false);
      if (partnerCode.trim()) {
        sessionStorage.removeItem('partnerCouponCode');
      }
      trackEvent('sign_up_complete', {
        method: 'email_2fa',
        template_id: selectedTemplateId || undefined,
      });
      toast.success('Conta verificada com sucesso!');

      const pendingAffiliateCode = sessionStorage.getItem('affiliateCode');
      const pendingPlan = sessionStorage.getItem('selectedPlan');
      if (pendingPlan) {
        const qs = pendingAffiliateCode ? `?ref=${encodeURIComponent(pendingAffiliateCode)}` : '';
        navigate(`/planos${qs}`);
        return;
      }

      navigate(editorTarget);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível validar o código.');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResendTwoFactor = async () => {
    if (!twoFactorChallengeId) return;
    setResending2FA(true);
    try {
      const result = await resendTwoFactorCode(twoFactorChallengeId);
      setTwoFactorChallengeId(result?.challenge_id || twoFactorChallengeId);
      setTwoFactorMaskedEmail(result?.masked_email || twoFactorMaskedEmail);
      setTwoFactorCode('');
      toast.success('Novo código enviado para o e-mail cadastrado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível reenviar o código.');
    } finally {
      setResending2FA(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EF] flex">
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        jsonLd={seoConfig.jsonLd}
      />
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=1200)' }}
        />
        <div className="absolute inset-0 bg-[#E8B7B1]/60" />
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <BrandLogo size="md" withText={false} iconClassName="w-16 h-16 mb-6" />
          <h1 className="text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Comece Sua Jornada
          </h1>
          <p className="text-lg text-center text-white/80 max-w-md">
            Crie sua conta e monte a lista de presentes perfeita para seu grande dia.
          </p>
          <div className="mt-8 flex items-center gap-4 text-[#C8A97E]">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#C8A97E] flex items-center justify-center text-white font-bold">1</span>
              <span className="text-white/80">Crie sua conta</span>
            </div>
            <div className="w-8 h-[2px] bg-white/30" />
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-white font-bold">2</span>
              <span className="text-white/80">Monte seu site</span>
            </div>
            <div className="w-8 h-[2px] bg-white/30" />
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-white font-bold">3</span>
              <span className="text-white/80">Compartilhe!</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1C1C1C] mb-6">
              <BrandLogo size="sm" />
            </Link>
            <h2 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              {registerStep === 'form' ? 'Criar sua conta' : 'Confirmar cadastro'}
            </h2>
            <p className="text-[#333333] mt-2">
              {registerStep === 'form'
                ? 'Comece a montar seu site de casamento'
                : 'Digite o código enviado para finalizar a criação da conta'}
            </p>
            {registerStep === 'verify_2fa' && (
              <p className="mt-3 text-sm text-[#1C1C1C] bg-[#E8B7B1]/20 border border-[#E8B7B1]/35 rounded-lg px-3 py-2">
                Enviamos um código para <strong>{twoFactorMaskedEmail}</strong>.
              </p>
            )}
          </div>

          {selectedTemplate ? (
            <div className="mb-6 overflow-hidden rounded-2xl border border-[#E8B7B1]/30 bg-white shadow-[0_20px_40px_-34px_rgba(28,28,28,0.4)]">
              <div className="grid grid-cols-[92px_1fr] gap-4 p-4">
                <img
                  src={selectedTemplate.preview_image}
                  alt={selectedTemplate.label || selectedTemplate.name}
                  className="h-24 w-24 rounded-xl object-cover"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = TEMPLATE_PREVIEW_FALLBACK;
                  }}
                />
                <div className="min-w-0">
                  <p className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">
                    Template selecionado
                  </p>
                  <h3 className="mt-1 text-xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {selectedTemplate.label || selectedTemplate.name}
                  </h3>
                  <p className="mt-1 text-sm text-[#5B4A45]">
                    {selectedTemplate.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={templatePreviewTarget}
                      className="inline-flex items-center rounded-full border border-[#E8B7B1]/45 bg-[#FDF7F5] px-3 py-1.5 text-xs font-semibold text-[#B97D5B] transition hover:bg-[#F7ECE8]"
                    >
                      Ver prévia
                    </Link>
                    <span className="inline-flex items-center rounded-full bg-[#F6F1EF] px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#8B776F]">
                      {selectedTemplate.collection}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {registerStep === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Seu nome
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nome completo"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="partnerCode" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Cupom de parceiro
                </Label>
                <Input
                  id="partnerCode"
                  type="text"
                  value={partnerCode}
                  onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                  placeholder="Opcional"
                  autoComplete="off"
                  className="h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                  data-testid="register-partner-code-input"
                />
                <p className="mt-2 text-xs text-[#5B4A45]">
                  Se você recebeu um cupom de parceiro, aplique aqui para validar a taxa especial da sua conta.
                </p>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    autoComplete="new-password"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Confirmar senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Digite a senha novamente"
                    autoComplete="new-password"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full font-bold tracking-wider transition-colors duration-300 mt-6"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Criando conta...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Criar conta <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyTwoFactor} className="space-y-5">
              <div>
                <Label htmlFor="register-otp" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Código de verificação
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="register-otp"
                    type="text"
                    inputMode="numeric"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 tracking-[0.4em] text-center font-semibold focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="register-otp-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={verifying2FA}
                className="w-full h-12 bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full font-bold tracking-wider transition-colors duration-300"
                data-testid="register-2fa-submit-btn"
              >
                {verifying2FA ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Validando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Confirmar cadastro <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendTwoFactor}
                  disabled={resending2FA}
                  className="flex-1 rounded-full"
                >
                  {resending2FA ? 'Reenviando...' : 'Reenviar código'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setRegisterStep('form');
                    setTwoFactorCode('');
                    setTwoFactorChallengeId('');
                    setTwoFactorMaskedEmail('');
                  }}
                  className="flex-1 rounded-full"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
              </div>
            </form>
          )}

          {registerStep === 'form' && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#F6F1EF] text-[#333333]">ou continue com</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleRegister}
                className="w-full h-12 border-2 border-slate-200 hover:bg-slate-50 rounded-full font-medium transition-colors duration-300"
                data-testid="register-google-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar com Google
              </Button>
            </>
          )}

          <p className="text-center mt-6 text-[#333333]">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-[#1C1C1C] font-bold hover:underline" data-testid="login-link">
              Fazer login
            </Link>
          </p>

          <p className="text-center mt-4 text-xs text-[#333333]">
            Ao criar uma conta, você concorda com nossos Termos de Uso e Política de Privacidade.
          </p>
        </div>
      </div>
      <SupportWidget />
    </div>
  );
}
